import {
  ALL_RANGE_FILTER,
  AnyContent,
  Article,
  ContentItem,
  ContentType,
  FilterType,
  LiveArticleData,
  MONTH_RANGE_FILTER,
  NEW_RANGE_FILTER,
  Photo,
  PHOTO_TYPE,
  RangeFilter,
  StatusIdea,
  TEXT_TYPE,
  WEEK_RANGE_FILTER,
  YEAR_RANGE_FILTER
} from 'interfaces'
import { useState, useEffect, useRef, useMemo, useCallback, memo } from 'react'
import * as React from 'react'
import { ContentGridViewProps, GRID_BREAKPOINTS } from './types'
import { TIMERANGE_FILTER_DEFAULT } from 'config'
import { STOCK_CONTENT_PROVIDERS_ALL, StockContentProvider } from 'interfaces/Content/StockContentProvider'
import { dedupeArticles, dedupeMixedArticles, SortBy } from 'services/content/util'
import { getLastUsedSortBy, setLastUsedFilters, setLastUsedSortBy } from 'utils/filter-store'
import { useDispatch } from 'react-redux'
import ScrollListener from 'components/ScrollListener'
import LoadingCard from 'components/Card/LoadingCard'
import { Observable } from 'rxjs/Observable'
import 'rxjs/add/observable/fromEvent'
import { ALL_FILTER, arrayToRecord, ARTICLE_TYPE, STATUS_TYPE, STOCK_ALL_FILTER, STOCK_VIDEO_TYPE, VIDEO_TYPE } from 'shared'
import { SelectableContent } from 'services/content/state'
import { addLiveArticleData, setVisibleSelectableItems } from 'services/content'
import { resetScroll } from 'utils/dom'
import styles from './ContentGridView.pcss'
import ContentGridViewError from './ContentGridViewError'
import { ContentGridViewFilters } from './ContentGridViewFilters'
import AnyCard from 'components/Card'
import { checkFeatureAvailability, contentTypeFeatureMap, rangeFeatureMap } from 'services/product'
import { StoreThunkDispatch } from 'store/state'
import { ContentFetchParams, useContentFetch } from './useContentFetch'
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry'
import { useElementSize } from 'hooks/useElementSize'
import { MS_IN_DAY } from 'utils/constants'
import { mdiAlertCircleOutline } from '@mdi/js'
import Icon from '@mdi/react'

const timeRangeMs: Record<string, number> = {
  /* eslint-disable no-magic-numbers */
  [NEW_RANGE_FILTER]: MS_IN_DAY * 2,
  [WEEK_RANGE_FILTER]: MS_IN_DAY * 7,
  [MONTH_RANGE_FILTER]: MS_IN_DAY * 30,
  [YEAR_RANGE_FILTER]: MS_IN_DAY * 365,
  [ALL_RANGE_FILTER]: MS_IN_DAY * 365 * 3
  /* eslint-enable no-magic-numbers */
}

// eslint-disable-next-line no-magic-numbers
const LOADING_CARD_SIZES = [400, 260, 320, 380, 280, 320, 200, 320, 400, 260, 320, 380, 280, 320, 200, 320]

export const ContentGridView = memo((props: ContentGridViewProps) => {
  const dispatch = useDispatch<StoreThunkDispatch>()
  const { filter, range, contentTypes, sourceId, isStream, onItemShared } = props
  const [errorOffset, setErrorOffset] = useState(0)
  const [query, setQuery] = useState('')

  const [brokenPhotoIds, setBrokenPhotoIds] = useState<{ next: string[], handled: string[] }>({ next: [], handled: [] })
  const {
    content,
    error,
    isFetching,
    fetchParams,
    loadNextPage,
    setFetchParams
  } = useContentFetch({
    contentType: filter,
    sourceId,
    page: 0,
    sourceType: isStream ? 'stream' : 'feed',
    timeRange: props.range || TIMERANGE_FILTER_DEFAULT,
    sortBy: getLastUsedSortBy() as any || SortBy.Engagement,
    stockContentProvider: STOCK_CONTENT_PROVIDERS_ALL
  })
  const [gridContainerRef, { height, width }] = useElementSize<HTMLDivElement>()
  const [showLiveContentWarning, setShowLiveContentWarning] = useState(false)
  const scrollListenerRef = useRef<ScrollListener>(null)

  useEffect(() => {
    const contentDate = content[0]?.createdAt ? new Date(content[0].createdAt).getTime() : null
    let isContentOlderThanRange = false
    if (!isFetching && contentDate && range) {
      if (Date.now() - contentDate > timeRangeMs[range]) {
        isContentOlderThanRange = true
      }
    }
    setShowLiveContentWarning(isContentOlderThanRange)
  }, [content, range, isFetching])

  useEffect(() => {
    const padding = 80
    setErrorOffset(content.length > 0 ? height + padding : 0)
  }, [height, content.length])

  useEffect(() => {
    // EXPL: Make top row cards expand from the top of the grid to prevent overflow
    const gridElement = gridContainerRef.current
    if (props.preventTopOverflow && gridElement) {
      const columns = gridElement.getElementsByClassName(styles.masonry)[0].children
      const touchedNodesPrev = [...document.querySelectorAll('[data-grid-item-touched="true"]')]

      ;[...columns].map(col => col.children[0]).forEach((el) => {
        if (el) {
          (el as HTMLElement).style.transformOrigin = 'top'
          ;(el as HTMLElement).dataset.gridItemTouched = 'true'
          const index = touchedNodesPrev.indexOf(el)
          if (index !== -1) {
            touchedNodesPrev.splice(index, 1)
          }
        }
      })

      touchedNodesPrev.forEach((el: HTMLElement) => {
        el.style.transformOrigin = 'center'
        el.dataset.gridItemTouched = 'false'
      })
    }
  }, [width, content])

  useEffect(() => {
    const nextParams: Partial<ContentFetchParams> = { sourceId, contentType: filter }
    if (range && range !== fetchParams.timeRange) {
      nextParams.timeRange = range
      checkRangeLimits(range)
    }
    if (filter !== ALL_FILTER && filter !== STOCK_ALL_FILTER && contentTypes.indexOf(filter as any) === -1) {
      checkTypeLimits(filter as any)
    }
    setFetchParams(nextParams)
    setQuery('')
  }, [range, filter, sourceId, setFetchParams])

  const checkRangeLimits = (range: RangeFilter, skipUpgradePopup?: boolean) => {
    // NEW and WEEK are available to all, skip check
    // Skip check for Stock content.
    const rangeAllowed = props.noContentRangeCheck
      || (range === NEW_RANGE_FILTER || range === WEEK_RANGE_FILTER || filter.startsWith('stock'))
      || dispatch(checkFeatureAvailability(rangeFeatureMap[range], skipUpgradePopup, () => setLastUsedFilters(filter, WEEK_RANGE_FILTER)))

    if (!rangeAllowed && skipUpgradePopup) {
      onRangeChange(WEEK_RANGE_FILTER)
    }
  }

  const checkTypeLimits = (type: ContentType, skipUpgradePopup?: boolean) => {
    const typeAllowed = props.noContentTypeCheck
      || type === ALL_FILTER as string
      || dispatch(checkFeatureAvailability(contentTypeFeatureMap[type], skipUpgradePopup))

    if (!typeAllowed && skipUpgradePopup) {
      onFilterChange(ARTICLE_TYPE)
    }
  }

  useEffect(() => {
    if (![VIDEO_TYPE, STOCK_VIDEO_TYPE, STATUS_TYPE].includes(filter)) {
      dispatch(setVisibleSelectableItems(arrayToRecord(content as SelectableContent[] || [], 'id')))
    }
  }, [content, filter, dispatch])

  useEffect(() => {
    const windowResize$ = Observable
      .fromEvent(window, 'resize')
      .debounceTime(250) // eslint-disable-line no-magic-numbers
      .subscribe(() => {
        if (scrollListenerRef.current) {
          scrollListenerRef.current.emitScroll()
        }
      })
    return () => {
      windowResize$.unsubscribe()
      dispatch(setVisibleSelectableItems({}))
    }
  }, [dispatch])

  const onSortByChange = (value: SortBy) => {
    resetScroll()
    setLastUsedSortBy(value)
    setFetchParams({ sortBy: value })
    setQuery('')
  }

  const onRangeChange = (r: RangeFilter) => {
    resetScroll()
    if (props.onRangeChanged && props.onRangeChanged(r)) {
      return
    }
    setFetchParams({ timeRange: r })
    setQuery('')
  }

  const onFilterChange = (f: FilterType) => {
    props.onFilterChange(f)
    resetScroll()
  }

  const onStockSourceChange = (s: StockContentProvider) => {
    setFetchParams({ stockContentProvider: s })
    setQuery('')
  }

  const scrollElement = useMemo(() => {
    return props.scroll?.element || document.getElementsByTagName('main')[0]
  }, [props.scroll])

   
  const onCompose = useCallback((item: ContentItem) => {
    if (onItemShared) {
      return onItemShared(item as AnyContent)
    }
  }, [onItemShared])

  const onImageLoadingFailed = React.useCallback((item: ContentItem) => {
    if (item.hash) {
      setBrokenPhotoIds(current => {
        const next = current.next.slice()
        next.push(item.hash)
        return { next, handled: current.handled }
      })
    }
  }, [])

  const onLiveArticleDataFetched = useCallback((id: string, data: LiveArticleData) => {
    dispatch(addLiveArticleData({ [id]: { id, ...data } }))
  }, [dispatch])

  const contentCards = useMemo(() => {
    const brokenPhotos = brokenPhotoIds.next.concat(brokenPhotoIds.handled)
    if (content.length === 0) {
      return []
    }

    let items: any = content.filter(item => brokenPhotos.indexOf((item as any).hash) === -1)
    if (filter === ARTICLE_TYPE) {
      items = dedupeArticles(items as Article[])
    }

    if (filter === ALL_FILTER) {
      items = dedupeMixedArticles(items)
    }

    if (query) {
      const q = query.trim().toLowerCase()
      switch (filter) {
        case ARTICLE_TYPE:
        case VIDEO_TYPE:
          items = items.filter((item: Article) => {
            return item.title?.toLowerCase().includes(q)
              || item.description?.toLowerCase().includes(q)
              || item.status?.toLowerCase().includes(q)
          })
          break
        case PHOTO_TYPE:
          items = items.filter((item: Photo) => item.status?.toLowerCase().includes(q))
          break
        case STATUS_TYPE:
        case TEXT_TYPE:
          items = items.filter((item: StatusIdea) => {
            return item.status?.toLowerCase().includes(q) || item.author?.toLowerCase().includes(q)
          })
          break
      }
    }

    return items.map((item: any) => {
      const textCardProps = {
        onPin: props.onItemPinned,
        onSourceClick: props.onSourceClick as any,
        onFeedClick: props.onFeedClick
      }

      return (
        <AnyCard
          key={item.id}
          children={item}
          content={item}
          width={props.itemWidth}
          square={false}
          skipFeatureChecks={props.skipContentCardsFeatureChecks}
          onCompose={onCompose}
          onClick={props.onContentItemClick}
          onImageLoadingFailed={onImageLoadingFailed}
          onLiveArticleDataFetched={onLiveArticleDataFetched}
          {...(item.type === ARTICLE_TYPE ? textCardProps : {})}
        />
      )
    })
  }, [
    brokenPhotoIds.next,
    brokenPhotoIds.handled,
    content, filter,
    props.onItemPinned,
    props.onSourceClick,
    props.onFeedClick,
    props.itemWidth,
    props.skipContentCardsFeatureChecks,
    props.onContentItemClick,
    query,
    onCompose,
    onImageLoadingFailed,
    onLiveArticleDataFetched
  ])

  const loadingCards = React.useMemo(() => {
    const shouldRenderLoadingCards = isFetching || (content.length === 0 && !props.hideEmptyCards)
    if (!shouldRenderLoadingCards) {
      return []
    }
    return LOADING_CARD_SIZES.map((height) => {
      return (
        <LoadingCard
          width={props.itemWidth}
          height={height}
          active={isFetching}
        />
      )
    })
  }, [isFetching, content.length, props.hideEmptyCards, props.itemWidth])

  const withSearch = Boolean(query.trim())

  return (
    <div className={`${styles.view} ${props.className || ''}`} data-testid="content-grid">
      {!props.hideFilters && (
        <ContentGridViewFilters
          filter={filter}
          sortBy={fetchParams.sortBy}
          range={fetchParams.timeRange}
          contentTypes={contentTypes}
          hideRangeFilter={props.hideRangeFilter}
          hideSorting={props.hideSorting}
          className={props.filtersContainerClassName}
          feedType={props.feedType}
          stockContentSource={fetchParams.stockContentProvider}
          query={query}
          disableSearch={isFetching || content.length === 0}
          onQueryChange={setQuery}
          onFilterChange={onFilterChange}
          onRangeChange={onRangeChange}
          onStockSourceChange={onStockSourceChange}
          onSortByChange={onSortByChange}
        />
      )}
      {showLiveContentWarning && (
        <div className={styles.liveContentWarning}>
          <Icon path={mdiAlertCircleOutline} size="20px" />
          <span>The content below is the most recent available</span>
        </div>
      )}
      {contentCards.length + loadingCards.length > 0 && (
        <ScrollListener
          ref={scrollListenerRef}
          className={styles.bricks}
          disabled={isFetching}
          emitTreshold={250}
          onScrollDebounceTime={50}
          emitInitial={false}
          useWindow={props.scroll?.useWindow}
          scrollElement={scrollElement}
          onScroll={loadNextPage}
        >
          <div ref={gridContainerRef} data-testid="grid">
            <ResponsiveMasonry columnsCountBreakPoints={GRID_BREAKPOINTS}>
              <Masonry gutter="12px" className={styles.masonry}>
                {contentCards.concat(loadingCards)}
              </Masonry>
            </ResponsiveMasonry>
          </div>
        </ScrollListener>
      )}
      {!isFetching && error && !withSearch && (
        <ContentGridViewError
          filter={filter}
          hasItems={content.length > 0}
          error={error}
          offset={errorOffset}
          isStream={props.isStream}
          errorElement={props.errorElement}
          isFavoritesView={props.favoritesMode}
        />
      )}
      {!isFetching && withSearch && contentCards.length === 0 && (
        <div className={styles.noResults}>
          No results found.
        </div>
      )}
    </div>
  )
})
